import { useState, useEffect, useRef } from 'react';
import { api } from '../lib/api';
import { STYLE_PRESETS, AI_MODELS } from '../lib/constants';
import NarrationPanel from './NarrationPanel';

function SavedPOIs({ tourId, tourLang, onPOIClick, highlightedPOI, onPOIsChanged }) {
  const [pois, setPOIs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingNotes, setEditingNotes] = useState(null);
  const [notesText, setNotesText] = useState('');
  const [editingDescription, setEditingDescription] = useState(null);
  const [descriptionText, setDescriptionText] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  const [editingRadius, setEditingRadius] = useState(null);
  const [radiusValue, setRadiusValue] = useState(50);
  const [selectedPOIForNarration, setSelectedPOIForNarration] = useState(null);
  const poiRefs = useRef({});

  // Batch mode state
  const [batchMode, setBatchMode] = useState(false);
  const [selectedPOIs, setSelectedPOIs] = useState(new Set());
  const [batchTextOptions, setBatchTextOptions] = useState({
    style_preset: 'informative',
    max_chars: 1000,
    model: 'gpt-4o-mini',
  });
  const [voices, setVoices] = useState([]);
  const [selectedVoice, setSelectedVoice] = useState('alloy');
  const [selectedModel, setSelectedModel] = useState(null);
  const [batchProgress, setBatchProgress] = useState(null); // { current: 2, total: 5, type: 'text' | 'audio' }
  const [batchError, setBatchError] = useState(null);

  // Load voices on mount
  useEffect(() => {
    loadVoices();
  }, []);

  // Auto-select model when voice changes
  useEffect(() => {
    const voice = voices.find(v => v.id === selectedVoice);
    if (voice && voice.models && voice.models.length > 0) {
      const defaultModel = voice.models.find(m => m.is_default);
      setSelectedModel(defaultModel ? defaultModel.id : voice.models[0].id);
    } else {
      setSelectedModel(null);
    }
  }, [selectedVoice, voices]);

  useEffect(() => {
    if (tourId) {
      loadSavedPOIs();
    }
  }, [tourId]);

  useEffect(() => {
    if (highlightedPOI) {
      // Small delay to ensure DOM is rendered after tab switch
      const timer = setTimeout(() => {
        if (poiRefs.current[highlightedPOI]) {
          poiRefs.current[highlightedPOI].scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [highlightedPOI]);
  const loadVoices = async () => {
    try {
      const response = await api.getVoices();
      const voicesList = response.voices || response.Voices || [];
      if (voicesList.length > 0) {
        setVoices(voicesList);
        // Set default voice
        const defaultVoice = voicesList.find(v => v.id === 'alloy') || voicesList[0];
        setSelectedVoice(defaultVoice.id);
      }
    } catch (error) {
      console.error('Failed to load voices:', error);
    }
  };
  const loadSavedPOIs = async () => {
    setLoading(true);
    try {
      const response = await api.getSavedPOIs(tourId);
      setPOIs(response.pois || []);
      if (onPOIsChanged) {
        onPOIsChanged(response.pois || []);
      }
    } catch (error) {
      console.error('Failed to load saved POIs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (poiId) => {
    if (!confirm('Are you sure you want to remove this POI from the tour?')) {
      return;
    }

    try {
      await api.deleteSavedPOI(tourId, poiId);
      const updatedPOIs = pois.filter(poi => poi.id !== poiId);
      setPOIs(updatedPOIs);
      if (onPOIsChanged) {
        onPOIsChanged(updatedPOIs);
      }
    } catch (error) {
      alert('Failed to delete POI: ' + error.message);
    }
  };

  const handleEditNotes = (poi) => {
    setEditingNotes(poi.id);
    setNotesText(poi.notes || '');
  };

  const handleEditDescription = (poi) => {
    setEditingDescription(poi.id);
    setDescriptionText(poi.description || '');
  };

  const handleEditRadius = (poi) => {
    setEditingRadius(poi.id);
    setRadiusValue(poi.radius_m || 50);
  };

  const handleSaveNotes = async (poiId) => {
    setSavingNotes(true);
    try {
      const updated = await api.updateSavedPOI(tourId, poiId, {
        notes: notesText || null,
      });
      setPOIs(pois.map(poi => poi.id === poiId ? updated : poi));
      setEditingNotes(null);
    } catch (error) {
      alert('Failed to save notes: ' + error.message);
    } finally {
      setSavingNotes(false);
    }
  };

  const handleSaveDescription = async (poiId) => {
    setSavingNotes(true);
    try {
      const updated = await api.updateSavedPOI(tourId, poiId, {
        description: descriptionText || null,
      });
      setPOIs(pois.map(poi => poi.id === poiId ? updated : poi));
      setEditingDescription(null);
    } catch (error) {
      alert('Failed to save description: ' + error.message);
    } finally {
      setSavingNotes(false);
    }
  };

  const handleCancelNotes = () => {
    setEditingNotes(null);
    setNotesText('');
  };

  const handleCancelDescription = () => {
    setEditingDescription(null);
    setDescriptionText('');
  };

  const handleCancelRadius = () => {
    setEditingRadius(null);
    setRadiusValue(50);
  };

  const handleSaveRadius = async (poiId) => {
    if (radiusValue < 10 || radiusValue > 500) {
      alert('Radius must be between 10 and 500 meters');
      return;
    }

    setSavingNotes(true);
    try {
      const updated = await api.updateSavedPOI(tourId, poiId, {
        radius_m: radiusValue,
      });
      setPOIs(pois.map(poi => poi.id === poiId ? updated : poi));
      setEditingRadius(null);
      if (onPOIsChanged) {
        onPOIsChanged(pois.map(poi => poi.id === poiId ? updated : poi));
      }
    } catch (error) {
      alert('Failed to save radius: ' + error.message);
    } finally {
      setSavingNotes(false);
    }
  };

  const getTierColor = (tier) => {
    const colors = {
      'landmark': '#ff4444',
      'notable': '#ff9944',
      'minor': '#44aaff',
    };
    return colors[tier] || '#666';
  };

  const getTierLabel = (tier) => {
    const labels = {
      'landmark': 'Landmark',
      'notable': 'Notable',
      'minor': 'Minor',
    };
    return labels[tier] || tier;
  };

  // Batch selection handlers
  const handleTogglePOI = (poiId) => {
    const newSelected = new Set(selectedPOIs);
    if (newSelected.has(poiId)) {
      newSelected.delete(poiId);
    } else {
      newSelected.add(poiId);
    }
    setSelectedPOIs(newSelected);
  };

  const handleSelectAll = () => {
    setSelectedPOIs(new Set(pois.map(p => p.id)));
  };

  const handleSelectMissingText = () => {
    setSelectedPOIs(new Set(pois.filter(p => !p.has_narration).map(p => p.id)));
  };

  const handleSelectMissingAudio = () => {
    setSelectedPOIs(new Set(pois.filter(p => !p.has_audio).map(p => p.id)));
  };

  const handleClearSelection = () => {
    setSelectedPOIs(new Set());
  };

  // Batch generation handlers
  const handleBatchGenerateText = async () => {
    if (selectedPOIs.size === 0) {
      alert('Please select at least one POI');
      return;
    }

    const poisToProcess = Array.from(selectedPOIs);
    setBatchProgress({ current: 0, total: poisToProcess.length, type: 'text' });
    setBatchError(null);
    let successCount = 0;
    let failCount = 0;
    const errors = [];

    for (let i = 0; i < poisToProcess.length; i++) {
      try {
        const narration = await api.generateNarration(poisToProcess[i], {
          lang: tourLang || 'en',
          ...batchTextOptions,
        });

        // Ensure the newly generated narration becomes the active one for this point
        if (narration && narration.id) {
          await api.activateNarration(narration.id);
        }

        successCount++;
        setBatchProgress({ current: i + 1, total: poisToProcess.length, type: 'text' });
      } catch (error) {
        failCount++;
        const poi = pois.find(p => p.id === poisToProcess[i]);
        errors.push(`${poi?.name || 'POI'}: ${error.message}`);
        console.error(`Failed to generate text for POI ${poisToProcess[i]}:`, error);
      }
    }

    setBatchProgress(null);
    await loadSavedPOIs();
    
    if (failCount > 0) {
      const errorDetails = errors.length > 0 ? '\n' + errors.slice(0, 3).join('\n') : '';
      setBatchError(`Generated text for ${successCount}/${poisToProcess.length} POIs (${failCount} failed)${errorDetails}`);
    } else {
      setBatchError(`✓ Generated text for ${successCount} POIs`);
    }
    
    setTimeout(() => setBatchError(null), 10000);
  };

  const handleBatchGenerateAudio = async () => {
    if (selectedPOIs.size === 0) {
      alert('Please select at least one POI');
      return;
    }

    const poisToProcess = Array.from(selectedPOIs);
    setBatchProgress({ current: 0, total: poisToProcess.length, type: 'audio' });
    setBatchError(null);
    let successCount = 0;
    let failCount = 0;
    const errors = [];

    for (let i = 0; i < poisToProcess.length; i++) {
      try {
        // Get active narration for this POI
        const response = await api.getNarrations(poisToProcess[i]);
        const narrations = response.narrations || [];
        const activeNarration = narrations.find(n => n.status === 'active');
        
        if (activeNarration) {
          await api.generateAudio(activeNarration.id, selectedVoice, selectedModel);
          successCount++;
        } else {
          failCount++;
          const poi = pois.find(p => p.id === poisToProcess[i]);
          errors.push(`${poi?.name || 'POI'}: No active narration`);
          console.warn(`No active narration for POI ${poisToProcess[i]}`);
        }
        
        setBatchProgress({ current: i + 1, total: poisToProcess.length, type: 'audio' });
      } catch (error) {
        failCount++;
        const poi = pois.find(p => p.id === poisToProcess[i]);
        errors.push(`${poi?.name || 'POI'}: ${error.message}`);
        console.error(`Failed to generate audio for POI ${poisToProcess[i]}:`, error);
      }
    }

    setBatchProgress(null);
    await loadSavedPOIs();
    
    if (failCount > 0) {
      const errorDetails = errors.length > 0 ? '\n' + errors.slice(0, 3).join('\n') : '';
      setBatchError(`Generated audio for ${successCount}/${poisToProcess.length} POIs (${failCount} failed)${errorDetails}`);
    } else {
      setBatchError(`✓ Generated audio for ${successCount} POIs`);
    }
    
    setTimeout(() => setBatchError(null), 10000);
  };

  if (loading) {
    return <div className="card"><p>Loading saved POIs...</p></div>;
  }

  if (pois.length === 0) {
    return (
      <div className="card">
        <h3>Saved POIs</h3>
        <p className="help-text">
          No POIs saved yet. Use the POI Discovery tool to find and save POIs to your tour.
        </p>
      </div>
    );
  }

  // If narration panel is open, show only that
  if (selectedPOIForNarration) {
    const poi = pois.find(p => p.id === selectedPOIForNarration);
    return (
      <div className="card">
        <div style={{ marginBottom: '1rem', paddingBottom: '0.75rem', borderBottom: '1px solid #e5e7eb' }}>
          <button
            onClick={() => setSelectedPOIForNarration(null)}
            style={{
              background: 'none',
              border: 'none',
              color: '#4f46e5',
              cursor: 'pointer',
              fontSize: '0.875rem',
              padding: 0,
              marginBottom: '0.5rem',
            }}
          >
            ← Back to POIs
          </button>
          {poi && (
            <h3 style={{ margin: 0, fontSize: '1rem' }}>{poi.name}</h3>
          )}
        </div>
        <NarrationPanel
          pointId={selectedPOIForNarration}
          lang={tourLang || 'en'}
          onClose={() => setSelectedPOIForNarration(null)}
        />
      </div>
    );
  }

  return (
    <div className="card">
      <h3>Spots ({pois.length})</h3>
      
      {/* Batch Mode Toggle */}
      <button
        onClick={() => {
          setBatchMode(!batchMode);
          if (batchMode) {
            setSelectedPOIs(new Set());
          }
        }}
        style={{
          width: '100%',
          padding: '0.75rem',
          marginTop: '1rem',
          marginBottom: '1rem',
          background: batchMode ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'white',
          color: batchMode ? 'white' : '#667eea',
          border: batchMode ? 'none' : '2px solid #667eea',
          borderRadius: '8px',
          cursor: 'pointer',
          fontWeight: '600',
          fontSize: '0.875rem',
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
          transition: 'all 0.2s ease',
        }}
      >
        {batchMode ? '▼' : '▶'} Batch Operations
      </button>

      {/* Batch Controls Panel */}
      {batchMode && (
        <div style={{ 
          padding: '1rem', 
          marginBottom: '1rem', 
          backgroundColor: '#f9fafb', 
          borderRadius: '8px',
          border: '1px solid #e5e7eb',
        }}>
          {/* Text Generation Settings */}
          <div style={{ marginBottom: '1rem', paddingBottom: '1rem', borderBottom: '1px solid #e5e7eb' }}>
            <h4 style={{ margin: '0 0 0.75rem 0', fontSize: '0.9rem', fontWeight: '600', color: '#1f2937' }}>
              Text Generation
            </h4>
            
            <div style={{ marginBottom: '0.75rem' }}>
              <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: '500' }}>
                AI Model
              </label>
              <select
                value={batchTextOptions.model}
                onChange={(e) => setBatchTextOptions({ ...batchTextOptions, model: e.target.value })}
                style={{ width: '100%', padding: '0.5rem', border: '2px solid #e1e8ed', borderRadius: '8px', fontSize: '0.875rem', backgroundColor: 'white' }}
              >
                {AI_MODELS.map(model => (
                  <option key={model.value} value={model.value}>
                    {model.label} - {model.description}
                  </option>
                ))}
              </select>
            </div>

            <div style={{ marginBottom: '0.75rem' }}>
              <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: '500' }}>
                Style Preset
              </label>
              <select
                value={batchTextOptions.style_preset}
                onChange={(e) => setBatchTextOptions({ ...batchTextOptions, style_preset: e.target.value })}
                style={{ width: '100%', padding: '0.5rem', border: '2px solid #e1e8ed', borderRadius: '8px', fontSize: '0.875rem', backgroundColor: 'white' }}
              >
                {STYLE_PRESETS.map(preset => (
                  <option key={preset.value} value={preset.value}>
                    {preset.label} - {preset.description}
                  </option>
                ))}
              </select>
            </div>

            <div style={{ marginBottom: '0.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: '500' }}>
                Max Characters: {batchTextOptions.max_chars}
              </label>
              <input
                type="range"
                min="300"
                max="5000"
                step="100"
                value={batchTextOptions.max_chars}
                onChange={(e) => setBatchTextOptions({ ...batchTextOptions, max_chars: parseInt(e.target.value) })}
                style={{ width: '100%' }}
              />
            </div>
          </div>

          {/* Audio Generation Settings */}
          <div style={{ marginBottom: '1rem', paddingBottom: '1rem', borderBottom: '1px solid #e5e7eb' }}>
            <h4 style={{ margin: '0 0 0.75rem 0', fontSize: '0.9rem', fontWeight: '600', color: '#1f2937' }}>
              Audio Generation
            </h4>
            
            <div style={{ marginBottom: '0.75rem' }}>
              <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: '500' }}>
                Voice
              </label>
              <select
                value={selectedVoice}
                onChange={(e) => setSelectedVoice(e.target.value)}
                style={{ width: '100%', padding: '0.5rem', border: '2px solid #e1e8ed', borderRadius: '8px', fontSize: '0.875rem', backgroundColor: 'white' }}
              >
                {voices.map(voice => (
                  <option key={voice.id} value={voice.id}>
                    {voice.name} ({voice.provider})
                  </option>
                ))}
              </select>
            </div>

            {selectedModel && (
              <div style={{ marginBottom: '0.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: '500' }}>
                  Model
                </label>
                <select
                  value={selectedModel}
                  onChange={(e) => setSelectedModel(e.target.value)}
                  style={{ width: '100%', padding: '0.5rem', border: '2px solid #e1e8ed', borderRadius: '8px', fontSize: '0.875rem', backgroundColor: 'white' }}
                >
                  {voices.find(v => v.id === selectedVoice)?.models?.map(model => (
                    <option key={model.id} value={model.id}>
                      {model.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {/* POI Selection */}
          <div style={{ marginBottom: '1rem', paddingBottom: '1rem', borderBottom: '1px solid #e5e7eb' }}>
            <h4 style={{ margin: '0 0 0.75rem 0', fontSize: '0.9rem', fontWeight: '600', color: '#1f2937' }}>
              Select POIs ({selectedPOIs.size} selected)
            </h4>
            
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              <button
                onClick={handleSelectAll}
                style={{
                  padding: '0.5rem 0.75rem',
                  fontSize: '0.75rem',
                  backgroundColor: 'white',
                  border: '2px solid #e1e8ed',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '500',
                  transition: 'all 0.2s',
                }}
              >
                All ({pois.length})
              </button>
              <button
                onClick={handleSelectMissingText}
                style={{
                  padding: '0.5rem 0.75rem',
                  fontSize: '0.75rem',
                  backgroundColor: 'white',
                  border: '2px solid #e1e8ed',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '500',
                  transition: 'all 0.2s',
                }}
              >
                Missing Text ({pois.filter(p => !p.has_narration).length})
              </button>
              <button
                onClick={handleSelectMissingAudio}
                style={{
                  padding: '0.5rem 0.75rem',
                  fontSize: '0.75rem',
                  backgroundColor: 'white',
                  border: '2px solid #e1e8ed',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '500',
                  transition: 'all 0.2s',
                }}
              >
                Missing Audio ({pois.filter(p => !p.has_audio).length})
              </button>
              <button
                onClick={handleClearSelection}
                style={{
                  padding: '0.5rem 0.75rem',
                  fontSize: '0.75rem',
                  backgroundColor: 'white',
                  border: '2px solid #e1e8ed',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '500',
                  transition: 'all 0.2s',
                }}
              >
                Clear
              </button>
            </div>
          </div>

          {/* Action Buttons */}
          <div>
            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem' }}>
              <button
                onClick={handleBatchGenerateText}
                disabled={selectedPOIs.size === 0 || batchProgress !== null}
                style={{
                  flex: 1,
                  padding: '0.75rem',
                  background: selectedPOIs.size === 0 || batchProgress !== null ? '#ccc' : 'linear-gradient(135deg, #2ecc71 0%, #27ae60 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontWeight: '600',
                  cursor: selectedPOIs.size === 0 || batchProgress !== null ? 'not-allowed' : 'pointer',
                  fontSize: '0.875rem',
                  boxShadow: selectedPOIs.size === 0 || batchProgress !== null ? 'none' : '0 2px 4px rgba(0, 0, 0, 0.1)',
                  transition: 'all 0.2s ease',
                }}
              >
                Generate Text
              </button>
              <button
                onClick={handleBatchGenerateAudio}
                disabled={selectedPOIs.size === 0 || batchProgress !== null}
                style={{
                  flex: 1,
                  padding: '0.75rem',
                  background: selectedPOIs.size === 0 || batchProgress !== null ? '#ccc' : 'linear-gradient(135deg, #3498db 0%, #2980b9 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontWeight: '600',
                  cursor: selectedPOIs.size === 0 || batchProgress !== null ? 'not-allowed' : 'pointer',
                  fontSize: '0.875rem',
                  boxShadow: selectedPOIs.size === 0 || batchProgress !== null ? 'none' : '0 2px 4px rgba(0, 0, 0, 0.1)',
                  transition: 'all 0.2s ease',
                }}
              >
                Generate Audio
              </button>
            </div>

            {/* Progress Indicator */}
            {batchProgress && (
              <div style={{ 
                padding: '0.75rem', 
                background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
                borderRadius: '8px',
                fontSize: '0.875rem',
                color: '#1e40af',
                textAlign: 'center',
                fontWeight: '500',
                border: '1px solid #93c5fd',
              }}>
                Processing {batchProgress.current}/{batchProgress.total} POIs 
                ({batchProgress.type === 'text' ? 'Text' : 'Audio'})...
              </div>
            )}

            {/* Status Message */}
            {batchError && (
              <div style={{ 
                padding: '0.75rem', 
                background: batchError.startsWith('✓') 
                  ? 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)' 
                  : 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)',
                borderRadius: '8px',
                fontSize: '0.875rem',
                color: batchError.startsWith('✓') ? '#065f46' : '#991b1b',
                textAlign: 'center',
                fontWeight: '500',
                border: batchError.startsWith('✓') ? '1px solid #6ee7b7' : '1px solid #fca5a5',
                whiteSpace: 'pre-line',
              }}>
                {batchError}
              </div>
            )}
          </div>
        </div>
      )}
      
      <div className="saved-poi-list">
        {pois.map((poi) => (
          <div
            key={poi.id}
            ref={(el) => poiRefs.current[poi.id] = el}
            className={`saved-poi-item ${highlightedPOI === poi.id ? 'highlighted' : ''}`}
          >
            {/* Checkbox for batch mode */}
            {batchMode && (
              <div style={{ marginBottom: '0.5rem' }}>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: '0.875rem' }}>
                  <input
                    type="checkbox"
                    checked={selectedPOIs.has(poi.id)}
                    onChange={() => handleTogglePOI(poi.id)}
                    style={{ marginRight: '0.5rem', width: '16px', height: '16px', cursor: 'pointer' }}
                  />
                  <span style={{ fontWeight: '500' }}>Select for batch</span>
                </label>
              </div>
            )}

            <div className="saved-poi-header">
              <div 
                className="saved-poi-title"
                onClick={() => onPOIClick && onPOIClick(poi)}
                style={{ cursor: onPOIClick ? 'pointer' : 'default' }}
              >
                <strong>{poi.name}</strong>
                {/* Status indicators */}
                <span style={{ marginLeft: '0.5rem', display: 'inline-flex', gap: '0.375rem' }}>
                  {poi.has_narration && (
                    <span
                      title="Narration available"
                      style={{
                        fontSize: '0.7rem',
                        padding: '3px 6px',
                        borderRadius: '4px',
                        backgroundColor: '#10b981',
                        color: 'white',
                        fontWeight: '600',
                        letterSpacing: '0.5px',
                      }}
                    >
                      TXT
                    </span>
                  )}
                  {poi.has_audio && (
                    <span
                      title="Audio available"
                      style={{
                        fontSize: '0.7rem',
                        padding: '3px 6px',
                        borderRadius: '4px',
                        backgroundColor: '#3b82f6',
                        color: 'white',
                        fontWeight: '600',
                        letterSpacing: '0.5px',
                      }}
                    >
                      MP3
                    </span>
                  )}
                </span>
              </div>
              <button
                className="button button-sm button-danger"
                onClick={() => handleDelete(poi.id)}
                title="Remove from tour"
                style={{ padding: '0.25rem 0.5rem', fontSize: '0.9rem' }}
              >
                ✕
              </button>
            </div>

            {editingDescription === poi.id ? (
              <div style={{ margin: '0.5rem 0' }}>
                <textarea
                  value={descriptionText}
                  onChange={(e) => setDescriptionText(e.target.value)}
                  placeholder="Add or edit description..."
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    fontSize: '0.85rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    resize: 'vertical',
                    minHeight: '60px',
                  }}
                  disabled={savingNotes}
                />
                <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                  <button
                    className="button button-sm button-primary"
                    onClick={() => handleSaveDescription(poi.id)}
                    disabled={savingNotes}
                  >
                    Save
                  </button>
                  <button
                    className="button button-sm button-secondary"
                    onClick={handleCancelDescription}
                    disabled={savingNotes}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <>
                {poi.description && (
                  <p className="saved-poi-description" style={{ fontSize: '0.85rem', margin: '0.5rem 0' }}>
                    {poi.description}
                  </p>
                )}
                <button
                  className="button button-sm button-secondary"
                  onClick={() => handleEditDescription(poi)}
                  style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', marginTop: '0.25rem' }}
                >
                  {poi.description ? 'Edit Description' : 'Add Description'}
                </button>
              </>
            )}

            <div className="saved-poi-meta">
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.8rem', color: '#6b7280' }}>
                <span>Radius:</span>
                {editingRadius === poi.id ? (
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <input
                      type="number"
                      value={radiusValue}
                      onChange={(e) => setRadiusValue(parseInt(e.target.value) || 50)}
                      min="10"
                      max="500"
                      style={{ width: '70px', padding: '0.25rem 0.5rem', fontSize: '0.8rem' }}
                      disabled={savingNotes}
                    />
                    <span style={{ fontSize: '0.75rem' }}>m</span>
                    <button
                      className="button button-sm button-primary"
                      onClick={() => handleSaveRadius(poi.id)}
                      disabled={savingNotes}
                      style={{ fontSize: '0.7rem', padding: '0.25rem 0.5rem' }}
                    >
                      ✓
                    </button>
                    <button
                      className="button button-sm button-secondary"
                      onClick={handleCancelRadius}
                      disabled={savingNotes}
                      style={{ fontSize: '0.7rem', padding: '0.25rem 0.5rem' }}
                    >
                      ✕
                    </button>
                  </div>
                ) : (
                  <span 
                    onClick={() => handleEditRadius(poi)}
                    style={{ 
                      cursor: 'pointer', 
                      textDecoration: 'underline', 
                      color: '#667eea',
                      fontWeight: '500'
                    }}
                  >
                    {poi.radius_m}m
                  </span>
                )}
              </div>
              {poi.wikidata_id && (
                <a
                  href={`https://www.wikidata.org/wiki/${poi.wikidata_id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="poi-link"
                  style={{ fontSize: '0.8rem' }}
                >
                  Wikidata ↗
                </a>
              )}
            </div>

            <div className="saved-poi-notes">
              {editingNotes === poi.id ? (
                <div>
                  <textarea
                    value={notesText}
                    onChange={(e) => setNotesText(e.target.value)}
                    placeholder="Add notes about this POI..."
                    rows={3}
                    style={{ width: '100%', marginBottom: '0.5rem', fontSize: '0.85rem', padding: '0.5rem' }}
                    disabled={savingNotes}
                  />
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button
                      className="button button-sm button-primary"
                      onClick={() => handleSaveNotes(poi.id)}
                      disabled={savingNotes}
                      style={{ fontSize: '0.8rem' }}
                    >
                      {savingNotes ? 'Saving...' : 'Save'}
                    </button>
                    <button
                      className="button button-sm button-secondary"
                      onClick={handleCancelNotes}
                      disabled={savingNotes}
                      style={{ fontSize: '0.8rem' }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div>
                  {poi.notes ? (
                    <div className="notes-display">
                      <p style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: '0.5rem', fontStyle: 'italic' }}>
                        {poi.notes}
                      </p>
                      <button
                        className="button button-sm button-text"
                        onClick={() => handleEditNotes(poi)}
                        style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                      >
                        Edit
                      </button>
                    </div>
                  ) : (
                    <button
                      className="button button-sm button-text"
                      onClick={() => handleEditNotes(poi)}
                      style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', color: '#9ca3af' }}
                    >
                      + Add Notes
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Narration button */}
            <div style={{ marginTop: '0.75rem', paddingTop: '0.75rem', borderTop: '1px solid #e5e7eb' }}>
              <button
                className="button button-sm"
                onClick={() => setSelectedPOIForNarration(poi.id)}
                style={{
                  width: '100%',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  fontSize: '0.8rem',
                  padding: '0.5rem',
                  borderRadius: '8px',
                  fontWeight: '600',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                  transition: 'all 0.2s ease',
                }}
              >
                Manage Narration
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default SavedPOIs;
